import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  LogOut, MapPin, CalendarDays, Network, Images, Plus, Pencil, Trash2, Users,
  LayoutDashboard, Eye, EyeOff, ArrowUp, ArrowDown, Play, Youtube,
} from 'lucide-react';
import { toast } from 'sonner';
import Button from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { ConfirmDialog } from '../../components/admin/Sheet';
import {
  NetworkDialog, CellDialog, EventDialog, GalleryDialog, HomeSectionDialog,
  PreachingDialog, ChannelDialog,
} from '../../components/admin/dialogs';
import RegistrationsSheet from '../../components/admin/RegistrationsSheet';
import CellsManager from '../../components/admin/CellsManager';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';

const TABS = [
  { id: 'networks', label: 'Redes', icon: Network },
  { id: 'cells', label: 'Células', icon: MapPin },
  { id: 'events', label: 'Eventos', icon: CalendarDays },
  { id: 'gallery', label: 'Fotos', icon: Images },
  { id: 'preachings', label: 'Pregações', icon: Play },
  { id: 'home', label: 'Home', icon: LayoutDashboard },
];


const SECTION_TYPE_LABELS = {
  hero: 'Banner principal',
  stats: 'Estatísticas',
  nextEvent: 'Próximo evento',
  mediaFeed: 'Momentos da Comunidade',
  recentPosts: 'Últimas Atividades',
  modules: 'Conteúdos por Categoria',
  resources: 'Recursos Rápidos',
  banners: 'Banners de conversão',
};


function StatCard({ icon: Icon, value, label, tone }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-soft">
      <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}>
        <Icon size={16} />
      </span>
      <p className="mt-2 font-heading text-xl font-extrabold text-ink">{value}</p>
      <p className="text-[11px] font-medium text-ink-soft">{label}</p>
    </div>
  );
}

function Row({ testid, thumb, thumbColor, title, subtitle, badge, extraAction, onEdit, onDelete }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-soft" data-testid={testid}>
      {thumb ? (
        <img src={thumb} alt="" className="h-11 w-11 shrink-0 rounded-lg object-cover" />
      ) : (
        <span className="h-11 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: thumbColor || '#0284c7' }} />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate font-heading font-bold text-ink">{title}</p>
        <p className="truncate text-xs text-ink-soft">{subtitle}</p>
      </div>
      {badge}
      {extraAction && (
        <button onClick={extraAction.onClick} className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-light text-primary hover:bg-primary hover:text-white" data-testid={extraAction.testid} aria-label={extraAction.label}>
          <extraAction.icon size={15} />
        </button>
      )}
      <button onClick={onEdit} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-ink-soft hover:text-primary" data-testid={`${testid}-edit`} aria-label="Editar">
        <Pencil size={15} />
      </button>
      <button onClick={onDelete} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-ink-soft hover:text-rose-500" data-testid={`${testid}-delete`} aria-label="Excluir">
        <Trash2 size={15} />
      </button>
    </div>
  );
}

export default function Dashboard() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const [networks, setNetworks] = useState([]);
  const [cells, setCells] = useState([]);
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [homeSections, setHomeSections] = useState([]);
  const [preachings, setPreachings] = useState([]);
  const [channels, setChannels] = useState([]);
  const [tab, setTab] = useState('networks');
  const [dialog, setDialog] = useState(null); // { type, entity }
  const [confirm, setConfirm] = useState(null); // { title, message, run }
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [regsEvent, setRegsEvent] = useState(null);

  function load() {
    Promise.all([
      api.getNetworks(), api.getCells(), api.getEvents(), api.getGallery(),
      api.getAllHomeSections(), api.getPreachings(), api.getPreachingChannels(),
    ]).then(
      ([n, c, e, g, hs, pr, ch]) => {
        setNetworks(n);
        setCells(c);
        setEvents(e);
        setGallery(g);
        setHomeSections(hs);
        setPreachings(pr);
        setChannels(ch);
      }
    );
  }

  useEffect(load, []);

  async function toggleVisibility(section) {
    await api.updateHomeSection(section.id, { visible: !section.visible });
    toast.success(section.visible ? 'Seção ocultada.' : 'Seção visível.');
    load();
  }

  async function moveSection(section, direction) {
    const res = await api.reorderHomeSection(section.id, direction);
    if (res.ok) load();
  }


  const netById = Object.fromEntries(networks.map((n) => [n.id, n]));

  function askDelete(title, message, run) {
    setConfirm({ title, message, run });
  }
  async function runConfirm() {
    if (!confirm) return;
    setConfirmLoading(true);
    try {
      await confirm.run();
      toast.success('Excluído com sucesso.');
      load();
      setConfirm(null);
    } catch (err) {
      toast.error('Não foi possível excluir. Tente novamente.');
    } finally {
      setConfirmLoading(false);
    }
  }

  const addLabel = {
    networks: 'Nova rede',
    cells: 'Nova célula',
    events: 'Novo evento',
    gallery: 'Adicionar foto',
    preachings: 'Nova pregação',
    home: 'Nova seção',
  }[tab];
  const addType = { networks: 'network', cells: 'cell', events: 'event', gallery: 'gallery', preachings: 'preaching', home: 'homeSection' }[tab];

  const channelById = Object.fromEntries(channels.map((c) => [c.id, c]));
  const sortedPreachings = [...preachings].sort((a, b) => (a.date < b.date ? 1 : -1));



  return (
    <div className="pb-8">
      <header className="sticky top-0 z-30 border-b border-slate-200/60 bg-white/85 py-4 backdrop-blur-xl">
        <div className="app-container flex items-center justify-between">
          <div>
            <h1 className="font-heading text-xl font-extrabold text-ink">Painel Admin</h1>
            <p className="text-xs text-ink-soft">{auth?.user?.email}</p>
          </div>
          <button
            onClick={() => { logout(); toast.success('Sessão encerrada.'); navigate('/'); }}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-ink-soft hover:text-rose-500"
            data-testid="admin-logout"
            aria-label="Sair"
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <div className="app-container py-4">
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
          <StatCard icon={Network} value={networks.length} label="Redes" tone="bg-accent-light text-accent-dark" />
          <StatCard icon={MapPin} value={cells.length} label="Células" tone="bg-primary-light text-primary" />
          <StatCard icon={CalendarDays} value={events.length} label="Eventos" tone="bg-emerald-100 text-emerald-600" />
          <StatCard icon={Images} value={gallery.length} label="Fotos" tone="bg-violet-100 text-violet-600" />
        </div>

        <div className="no-scrollbar mt-5 flex gap-2 overflow-x-auto rounded-full border border-slate-200 bg-white p-1 md:flex-wrap md:overflow-visible">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold font-heading transition-colors duration-200 ${tab === t.id ? 'bg-primary text-white' : 'text-ink-soft'}`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab !== 'cells' && (
          <Button className="mt-4 w-full md:max-w-xs" onClick={() => setDialog({ type: addType })} data-testid="admin-add-btn">
            <Plus size={18} /> {addLabel}
          </Button>
        )}

        {tab === 'cells' && (
          <div className="mt-4">
            <CellsManager
              cells={cells}
              networks={networks}
              onEdit={(cell) => setDialog({ type: 'cell', entity: cell })}
              onDelete={(cell) => askDelete('Excluir célula?', `A célula "${cell.name}" será removida.`, () => api.deleteCell(cell.id))}
              onView={(cell) => window.open(`/celula/${cell.id}`, '_blank', 'noreferrer')}
            />
          </div>
        )}

        <div className="mt-3 space-y-2.5">

          {tab === 'networks' && networks.map((n) => (
            <Row
              key={n.id}
              testid={`admin-network-${n.id}`}
              thumbColor={n.color}
              title={n.name}
              subtitle={`Supervisor: ${n.supervisor?.name || '—'}${n.mission ? ` · Missão: ${n.mission}` : ''}`}
              badge={<Badge tone="neutral">{cells.filter((c) => c.networkId === n.id).length} células</Badge>}
              onEdit={() => setDialog({ type: 'network', entity: n })}
              onDelete={() => askDelete('Excluir rede?', `A rede "${n.name}" e suas células serão removidas.`, () => api.deleteNetwork(n.id))}
            />
          ))}


          {tab === 'events' && events.map((e) => (
            <Row
              key={e.id}
              testid={`admin-event-${e.id}`}
              thumb={e.cover}
              title={e.title}
              subtitle={`${format(parseISO(e.date), "dd/MM · HH:mm", { locale: ptBR })} · ${e.filledSlots}/${e.totalSlots} vagas`}
              badge={e.filledSlots >= e.totalSlots ? <Badge tone="danger">Esgotado</Badge> : <Badge tone="success">Aberto</Badge>}
              extraAction={{ icon: Users, label: 'Ver inscritos', testid: `admin-event-${e.id}-regs`, onClick: () => setRegsEvent(e) }}
              onEdit={() => setDialog({ type: 'event', entity: e })}
              onDelete={() => askDelete('Excluir evento?', `O evento "${e.title}" será removido.`, () => api.deleteEvent(e.id))}
            />
          ))}

          {tab === 'gallery' && gallery.map((g) => (
            <Row
              key={g.id}
              testid={`admin-gallery-${g.id}`}
              thumb={g.img}
              title={g.title}
              subtitle={g.category === 'celula' ? 'Célula' : g.category === 'rede' ? 'Rede' : 'Evento'}
              onEdit={() => setDialog({ type: 'gallery', entity: g })}
              onDelete={() => askDelete('Excluir foto?', `A foto "${g.title}" será removida.`, () => api.deleteGalleryItem(g.id))}
            />
          ))}

          {tab === 'preachings' && (
            <>
              <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-soft">
                <p className="font-heading text-sm font-bold text-ink">Canais</p>
                <p className="text-xs text-ink-soft">Gerencie os canais de pregação das comunidades.</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {channels.map((ch) => (
                    <span key={ch.id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-canvas px-3 py-1.5 text-xs font-semibold text-ink">
                      <Youtube size={13} className="text-red-600" /> {ch.name}
                      <button
                        onClick={() => setDialog({ type: 'channel', entity: ch })}
                        className="ml-1 text-ink-soft hover:text-primary"
                        aria-label={`Editar canal ${ch.name}`}
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => askDelete('Excluir canal?', `O canal "${ch.name}" será removido.`, () => api.deletePreachingChannel(ch.id))}
                        className="text-ink-soft hover:text-rose-500"
                        aria-label={`Excluir canal ${ch.name}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </span>
                  ))}
                  <button
                    onClick={() => setDialog({ type: 'channel' })}
                    className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold text-ink-soft hover:text-primary"
                  >
                    <Plus size={13} /> Novo canal
                  </button>
                </div>
              </div>

              {sortedPreachings.map((p) => (
                <Row
                  key={p.id}
                  testid={`admin-preaching-${p.id}`}
                  thumb={`https://img.youtube.com/vi/${p.videoId}/hqdefault.jpg`}
                  title={p.title}
                  subtitle={`${channelById[p.channelId]?.name || '—'} · ${p.preacher} · ${format(parseISO(p.date), "dd/MM/yyyy", { locale: ptBR })}`}
                  badge={<Badge tone="neutral">{p.views.toLocaleString('pt-BR')} views</Badge>}
                  onEdit={() => setDialog({ type: 'preaching', entity: p })}
                  onDelete={() => askDelete('Excluir pregação?', `A pregação "${p.title}" será removida.`, () => api.deletePreaching(p.id))}
                />
              ))}
            </>
          )}

          {tab === 'home' && homeSections.length === 0 && (

            <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-white py-16 text-center" data-testid="admin-home-empty">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-light text-primary">
                <LayoutDashboard size={26} />
              </span>
              <h3 className="mt-4 font-heading text-lg font-extrabold text-ink">Nenhuma seção configurada</h3>
              <p className="mt-1 max-w-sm text-sm text-ink-soft">
                A página inicial está vazia. Adicione a primeira seção para começar a montar a Home.
              </p>
              <Button className="mt-5" onClick={() => setDialog({ type: 'homeSection' })} data-testid="admin-home-add-first">
                <Plus size={18} /> Adicionar primeira seção
              </Button>
            </div>
          )}

          {tab === 'home' && homeSections.map((s, i) => (
            <div
              key={s.id}
              data-testid={`admin-home-${s.id}`}
              className={`flex items-center gap-3 rounded-xl border bg-white p-3 shadow-soft ${s.visible ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}
            >
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => moveSection(s, 'up')}
                  disabled={i === 0}
                  className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-ink-soft hover:text-primary disabled:opacity-30"
                  aria-label="Mover para cima"
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  onClick={() => moveSection(s, 'down')}
                  disabled={i === homeSections.length - 1}
                  className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-ink-soft hover:text-primary disabled:opacity-30"
                  aria-label="Mover para baixo"
                >
                  <ArrowDown size={14} />
                </button>
              </div>

              <span className="flex h-11 w-1.5 shrink-0 rounded-full bg-primary" />

              <div className="min-w-0 flex-1">
                <p className="truncate font-heading font-bold text-ink">{s.title}</p>
                <p className="truncate text-xs text-ink-soft">
                  {SECTION_TYPE_LABELS[s.type] || s.type} · Ordem {s.order}
                </p>
              </div>

              <Badge tone={s.visible ? 'success' : 'neutral'}>
                {s.visible ? 'Visível' : 'Oculta'}
              </Badge>

              <button
                onClick={() => toggleVisibility(s)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-ink-soft hover:text-primary"
                aria-label={s.visible ? 'Ocultar' : 'Mostrar'}
              >
                {s.visible ? <Eye size={15} /> : <EyeOff size={15} />}
              </button>
              <button
                onClick={() => setDialog({ type: 'homeSection', entity: s })}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-ink-soft hover:text-primary"
                aria-label="Editar"
              >
                <Pencil size={15} />
              </button>
              <button
                onClick={() => askDelete('Excluir seção?', `A seção "${s.title}" será removida da Home.`, () => api.deleteHomeSection(s.id))}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-ink-soft hover:text-rose-500"
                aria-label="Excluir"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>


      {dialog?.type === 'network' && (
        <NetworkDialog network={dialog.entity} onClose={() => setDialog(null)} onSaved={load} />
      )}
      {dialog?.type === 'cell' && (
        <CellDialog cell={dialog.entity} networks={networks} onClose={() => setDialog(null)} onSaved={load} />
      )}
      {dialog?.type === 'event' && (
        <EventDialog event={dialog.entity} onClose={() => setDialog(null)} onSaved={load} />
      )}
      {dialog?.type === 'gallery' && (
        <GalleryDialog item={dialog.entity} onClose={() => setDialog(null)} onSaved={load} />
      )}
      {dialog?.type === 'homeSection' && (
        <HomeSectionDialog section={dialog.entity} onClose={() => setDialog(null)} onSaved={load} />
      )}
      {dialog?.type === 'preaching' && (
        <PreachingDialog preaching={dialog.entity} channels={channels} onClose={() => setDialog(null)} onSaved={load} />
      )}
      {dialog?.type === 'channel' && (
        <ChannelDialog channel={dialog.entity} onClose={() => setDialog(null)} onSaved={load} />
      )}

      {confirm && (


        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          onCancel={() => setConfirm(null)}
          onConfirm={runConfirm}
          loading={confirmLoading}
        />
      )}

      {regsEvent && (
        <RegistrationsSheet event={regsEvent} onClose={() => setRegsEvent(null)} />
      )}
    </div>
  );
}
